// src/pages/ChooseAction.tsx
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight, MapPin } from "lucide-react";

import Person01DepartamentoGovernancaPng from "../assets/characters/choose-action/person01-departamento-governanca.png";
import Person01DepartamentoTecnicoPng from "../assets/characters/choose-action/person01-departamento-tecnico.png";

import { useAuth } from "../context/auth-context";

type MenuItemType = "route" | "function";

type ModulePermissions = {
  canView?: boolean | null;
  canCreate?: boolean | null;
  canUpdate?: boolean | null;
  canDelete?: boolean | null;
  canReport?: boolean | null;
};

type AddressOrigin = {
  value: string;
  tipoLabel: string;
  name: string;
  display: string;
};

type SubItem = {
  label: string;
  codigo?: string;
  tipoItem?: MenuItemType;
  tipo_item?: MenuItemType;
  actionKey?: string | null;
  action_key?: string | null;
  routeEnabled?: boolean;
  route_enabled?: boolean;
  developed?: boolean;
  devOnly?: boolean;
  children?: SubItem[];
};

type MainItem = {
  id?: number | string | null;
  label: string;
  codigo?: string;
  subitems: SubItem[];
  themeColor?: string;
  developed?: boolean;
  devOnly?: boolean;
  permissions?: ModulePermissions | null;
  addressOrigins?: AddressOrigin[];
};

type Department = {
  id?: number | string | null;
  name: string;
  slug?: string;
  routeKey?: string;
  themeColor?: string;
  iconKey?: string;
  ordem?: number | null;
  items: MainItem[];
};

type AddressOption = {
  value: string;
  label: string;
  lines: string[];
  tipoLabel?: string;
  isMatriz?: boolean;
  isAll?: boolean;
};

type SessaoEndereco = {
  id?: number | string | null;
  label?: string | null;
  nome?: string | null;

  tipo_unidade?: string | null;
  tipoUnidade?: string | null;
  tipo?: string | null;
  tipo_endereco?: string | null;
  tipoEndereco?: string | null;

  is_matriz?: boolean | string | number | null;
  isMatriz?: boolean | string | number | null;
  matriz?: boolean | string | number | null;

  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
};

type SessaoEnderecoComModulos = SessaoEndereco & {
  departamentos_acessiveis?: any[];
};

type ChooseActionCharacterType = "governanca" | "tecnico" | "default";

type DefaultChooseActionSvgProps = {
  accentColor: string;
};

const ALL_ADDRESSES_VALUE = "__ALL__";
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";
const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas", "saídas"]);

const CHARACTER_VERTICAL_OFFSET = 26;

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const getChooseActionCharacterType = (
  pathname: string
): ChooseActionCharacterType => {
  const cleanPath = pathname.endsWith("/") ? pathname : `${pathname}/`;

  if (cleanPath.startsWith("/governanca-qualidade-e-compliance/")) {
    return "governanca";
  }

  if (cleanPath.startsWith("/departamento-tecnico/")) {
    return "tecnico";
  }

  return "default";
};

const DefaultChooseActionSvg: React.FC<DefaultChooseActionSvgProps> = ({
  accentColor,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 130 527.087 670"
    role="img"
    aria-label="Escolher ação"
    preserveAspectRatio="xMidYMid meet"
    style={{
      width: "100%",
      maxWidth: 330,
      height: 430,
      display: "block",
      overflow: "visible",
    }}
  >
    <g transform="translate(-650.145 -138)">
      <path
        d="M381.944,699.813s6.572,18.987,12.414,37.243,33.592,27.75,33.592,27.75l5.112,13.145-15.336,7.3L373.181,774.3s1.461-14.605,1.461-19.717-23.369-48.2-23.369-48.2Z"
        transform="translate(622.06 147.255)"
        fill="#ed9da0"
      />

      <circle
        cx="18"
        cy="18"
        r="18"
        transform="translate(1086.285 760.252)"
        fill={accentColor}
      />

      <circle
        cx="16"
        cy="16"
        r="16"
        transform="translate(816.573 525.509)"
        fill={accentColor}
      />

      <circle
        cx="9"
        cy="9"
        r="9"
        transform="translate(1030 290)"
        fill={accentColor}
      />

      <path
        d="M290.341,689.068s-22.638,30.671-27.75,35.783-6.572,24.1-6.572,24.1l8.763,20.447L259.67,779.62l-39.434-39.434,42.355-62.8Z"
        transform="translate(590.978 141.935)"
        fill="#ed9da0"
      />

      <path
        d="M148.236,337.215c-9.031-.726-29.28-83.7-37.317-129.125-.021-.12-.042-.24-.064-.36-20.6,60.036-41.342,118.042-45.512,120.822-8.763,5.844-51.922-30.827-64.334-39.59-11.823-8.347,83.889-160,93.006-174.38,0,0,0-.005,0-.007A230.774,230.774,0,0,0,86.673,86.9C70.608,42.356,98.357,0,98.357,0h38.7a23.023,23.023,0,0,1,23.131,22.442l.967,38.168,5.112,8.766s31.453,234.553,31.453,241.854c0,7.052-29.1,26.058-47.543,26.059Q149.188,337.29,148.236,337.215Z"
        transform="translate(821.666 570.297)"
        fill="#090814"
      />

      <path
        d="M260.422,763.908s0-8.763,2.82-13.136,8.134,15.327,8.134,15.327,32.132,8.763,17.526,18.257-57.691-20.447-70.105-28.48-8.033-17.526-1.461-24.1L230.116,719Z"
        transform="translate(588.765 151.805)"
        fill="#090814"
      />

      <circle
        cx="30.671"
        cy="30.671"
        r="30.671"
        transform="translate(900.339 366.836)"
        fill="#ed9da0"
      />

      <path
        d="M311.616,349.223c0,4.382-13.467,35.025-13.467,35.025l32.094,7.638V353.668Z"
        transform="translate(609.459 64.095)"
        fill="#ed9da0"
      />

      <path
        d="M418.453,764.235s4.016,1.1-.365-.365-2.921-9.493-2.741-13.576,42.905,9.194,42.905,19.418-54.77,10.224-70.836,8.763-19.717,0-21.908-10.224,6.207-24.464,6.207-24.464Z"
        transform="translate(625.35 157.685)"
        fill="#090814"
      />

      <path
        d="M65.386,167.358c-1.573-1.78-3.238-3.666-5.021-5.628l-.458.043-.529-1.124c-1.905-2.075-3.942-4.227-6.134-6.42-6.429-6.429-18.712,8.24-24.422,8.24a26.565,26.565,0,0,0-10.873,2.475,6.146,6.146,0,0,1-6.294-.743,6.451,6.451,0,0,1-2.39-4.069C6.2,142.473,2.471,93.515.325,68.758A104.348,104.348,0,0,1,11.2,13.489L19.139,2.58A6.232,6.232,0,0,1,26.6.486s24.771,25.7,24.862,26.693c.388,4.26,3.337,13.7,18.7,29.069C86.978,73.064,87.073,87.74,86.7,91.835a8.085,8.085,0,0,0-.037.809,18.072,18.072,0,0,1-.743,5.433c-7.04,22.2-1.528,48.784,7.33,76.532a6.259,6.259,0,0,1-2.524,6.791c-.147.1-.276.185-.388.258l-.016.021a5.98,5.98,0,0,1-4.729,2.526C80.021,184.2,73.072,176.06,65.386,167.358Z"
        transform="translate(894.102 428.178)"
        fill={accentColor}
      />

      <g transform="translate(873.606 352.196)">
        <path
          d="M347.144,336.654l-5.506-6.405c.458,33.729,29.241,63.18,29.241,63.18l-64.541-7.083-1.631-8.947-3.372,7.368-7.88-2.2-2.267-5.455-4.863,2.914,9.978-43.373c9.854-41.377,39.9-35.308,39.9-35.308,41.274-.2,34.673,28.9,34.673,28.9Z"
          transform="translate(-286.324 -301.034)"
          fill="#2f2e41"
        />
      </g>

      <path
        d="M391.05,538.048,411.9,552.8a14.724,14.724,0,0,1,1.218,23.075h0a14.724,14.724,0,0,1-22.246-3.311L378.806,553.06,336.35,488.9a17.692,17.692,0,0,0,9.933-3.058c4.4-3.236,7.659-9.885,7.659-9.885Z"
        transform="translate(618.52 94.156)"
        fill="#ed9da0"
      />
    </g>
  </svg>
);

const toCleanString = (value: any): string => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const toBooleanLoose = (value: any, fallback = false): boolean => {
  if (value === undefined || value === null) return fallback;

  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    return value === 1;
  }

  const raw = String(value).trim().toLowerCase();

  if (["true", "1", "sim", "s", "yes", "y"].includes(raw)) return true;
  if (["false", "0", "nao", "não", "n", "no"].includes(raw)) return false;

  return fallback;
};

const normalizeTipoUnidade = (value: any): string | null => {
  const raw = toCleanString(value);
  if (!raw) return null;

  const normalized = removeAccents(raw).toLowerCase();

  if (normalized === "matriz") return "matriz";
  if (normalized === "filial") return "filial";
  if (normalized === "base") return "base";
  if (normalized === "escritorio") return "escritorio";
  if (normalized === "laboratorio") return "laboratorio";
  if (normalized === "operacional") return "operacional";

  return raw;
};

const getEnderecoTipoRaw = (end: SessaoEndereco): string | null => {
  return normalizeTipoUnidade(
    end?.tipo_unidade ??
      end?.tipoUnidade ??
      end?.tipo ??
      end?.tipo_endereco ??
      end?.tipoEndereco
  );
};

const isEnderecoMatriz = (end: SessaoEndereco): boolean => {
  const tipo = getEnderecoTipoRaw(end);

  return toBooleanLoose(
    end?.is_matriz ?? end?.isMatriz ?? end?.matriz,
    tipo === "matriz"
  );
};

const getEnderecoTipoLabel = (end: SessaoEndereco): string => {
  if (isEnderecoMatriz(end)) return "Matriz";

  const tipo = getEnderecoTipoRaw(end);

  if (tipo === "matriz") return "Matriz";
  if (tipo === "filial") return "Filial";
  if (tipo === "base") return "Base";
  if (tipo === "escritorio") return "Escritório";
  if (tipo === "laboratorio") return "Laboratório";
  if (tipo === "operacional") return "Operacional";

  return tipo ? String(tipo).toUpperCase() : "Unidade";
};

const getDeptPermKey = (d: Department) =>
  d.slug ? String(d.slug) : slugify(d.name);

const getDeptRouteKey = (d: Department) =>
  d.routeKey ? String(d.routeKey) : d.slug ? String(d.slug) : slugify(d.name);

const getMainSlug = (item: MainItem) => slugify(item.label);

const getItemKey = (item: MainItem) =>
  String(item.id ?? item.codigo ?? item.label).toLowerCase();

const cleanCodigoForRoute = (dept: Department, codigo?: string) => {
  const raw = String(codigo || "").trim();
  if (!raw) return "";

  const deptKey = slugify(getDeptRouteKey(dept));
  const parts = slugify(raw).split("-").filter(Boolean);

  if (!parts.length) return "";
  if (parts[0] === deptKey) parts.shift();
  if (parts[parts.length - 1] === deptKey) parts.pop();

  return parts.join("-");
};

const getSubSlug = (dept: Department, sub: SubItem) => {
  const cleaned = cleanCodigoForRoute(dept, sub.codigo);
  return cleaned || slugify(sub.label);
};

const isFunctionItem = (sub: SubItem) => {
  const tipoItem = sub.tipoItem || sub.tipo_item;
  const routeEnabled =
    sub.routeEnabled !== undefined
      ? sub.routeEnabled
      : sub.route_enabled !== undefined
      ? sub.route_enabled
      : true;

  return tipoItem === "function" || routeEnabled === false;
};

const isGenerateReport = (sub: SubItem) => {
  const actionKey = sub.actionKey || sub.action_key || "";
  const label = norm(sub.label || "");
  const codigo = norm(sub.codigo || "");

  return (
    actionKey === "generate_report" ||
    codigo === "gerar-relatorio" ||
    (label.includes("gerar") && label.includes("relatorio"))
  );
};

const shouldHideFunctionItem = (sub: SubItem) =>
  isFunctionItem(sub) || isGenerateReport(sub);

const mapPermissions = (mod: any): ModulePermissions | null => {
  const p = mod?.permissions;

  if (!p || typeof p !== "object") return null;

  return {
    canView:
      p.canView !== undefined && p.canView !== null ? p.canView === true : null,
    canCreate:
      p.canCreate !== undefined && p.canCreate !== null
        ? p.canCreate === true
        : null,
    canUpdate:
      p.canUpdate !== undefined && p.canUpdate !== null
        ? p.canUpdate === true
        : null,
    canDelete:
      p.canDelete !== undefined && p.canDelete !== null
        ? p.canDelete === true
        : null,
    canReport:
      p.canReport !== undefined && p.canReport !== null
        ? p.canReport === true
        : null,
  };
};

const mapSubItemRecursive = (sub: any): SubItem => ({
  label: String(sub?.label || ""),
  codigo: String(sub?.codigo || ""),
  tipoItem:
    sub?.tipoItem === "function" || sub?.tipo_item === "function"
      ? "function"
      : "route",
  actionKey:
    sub?.actionKey !== undefined && sub?.actionKey !== null
      ? String(sub.actionKey)
      : sub?.action_key !== undefined && sub?.action_key !== null
      ? String(sub.action_key)
      : null,
  routeEnabled:
    sub?.routeEnabled !== undefined
      ? sub.routeEnabled !== false
      : sub?.route_enabled !== undefined
      ? sub.route_enabled !== false
      : true,
  developed: sub?.developed === true,
  devOnly: sub?.devOnly === true || sub?.dev_only === true,
  children: Array.isArray(sub?.children)
    ? sub.children.map((child: any) => mapSubItemRecursive(child))
    : [],
});

const mergeAddressOrigins = (
  a?: AddressOrigin[],
  b?: AddressOrigin[]
): AddressOrigin[] => {
  const map = new Map<string, AddressOrigin>();

  for (const origin of [...(a || []), ...(b || [])]) {
    const key = String(origin.value || "").trim();
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, origin);
    }
  }

  return Array.from(map.values());
};

const mergeMainItem = (primary: MainItem, fallback: MainItem): MainItem => {
  return {
    ...primary,
    themeColor: primary.themeColor || fallback.themeColor,
    developed: primary.developed ?? fallback.developed,
    devOnly: primary.devOnly ?? fallback.devOnly,
    permissions: primary.permissions || fallback.permissions || null,
    subitems:
      Array.isArray(primary.subitems) && primary.subitems.length > 0
        ? primary.subitems
        : fallback.subitems || [],
    addressOrigins: mergeAddressOrigins(
      primary.addressOrigins,
      fallback.addressOrigins
    ),
  };
};

const makeAddressOrigin = (end: SessaoEndereco): AddressOrigin | null => {
  const option = mapAddressOption(end);

  if (!option.value || option.isAll) return null;

  const primaryName =
    option.lines?.[0] || option.label || `Endereço ${option.value}`;

  return {
    value: option.value,
    tipoLabel: option.tipoLabel || "Unidade",
    name: primaryName,
    display: `${option.tipoLabel || "Unidade"} | ${primaryName}`,
  };
};

const mapDepartmentFromApi = (
  dept: any,
  addressOrigin: AddressOrigin | null = null
): Department => ({
  id: dept?.id ?? null,
  name: String(dept?.nome || ""),
  slug: String(dept?.slug || ""),
  routeKey:
    dept?.routeKey !== undefined && dept?.routeKey !== null
      ? String(dept.routeKey)
      : dept?.route_key !== undefined && dept?.route_key !== null
      ? String(dept.route_key)
      : String(dept?.slug || ""),
  themeColor:
    dept?.themeColor !== undefined && dept?.themeColor !== null
      ? String(dept.themeColor)
      : dept?.theme_color !== undefined && dept?.theme_color !== null
      ? String(dept.theme_color)
      : "#0f333b",
  iconKey:
    dept?.iconKey !== undefined && dept?.iconKey !== null
      ? String(dept.iconKey)
      : dept?.icon_key !== undefined && dept?.icon_key !== null
      ? String(dept.icon_key)
      : "lucide:circle",
  ordem:
    dept?.ordem !== undefined && dept?.ordem !== null
      ? Number(dept.ordem)
      : null,
  items: Array.isArray(dept?.modulos)
    ? dept.modulos
        .filter((mod: any) => {
          const permissions = mapPermissions(mod);
          return permissions?.canView === false ? false : true;
        })
        .map((mod: any) => ({
          id: mod?.id ?? null,
          label: String(mod?.label || ""),
          codigo: String(mod?.codigo || ""),
          developed: mod?.developed === true,
          devOnly: mod?.devOnly === true || mod?.dev_only === true,
          permissions: mapPermissions(mod),
          themeColor:
            mod?.themeColor !== undefined && mod?.themeColor !== null
              ? String(mod.themeColor)
              : mod?.theme_color !== undefined && mod?.theme_color !== null
              ? String(mod.theme_color)
              : dept?.themeColor ?? dept?.theme_color ?? "#0f333b",
          subitems: Array.isArray(mod?.subitems)
            ? mod.subitems.map((sub: any) => mapSubItemRecursive(sub))
            : [],
          addressOrigins: addressOrigin ? [addressOrigin] : [],
        }))
    : [],
});

function mapAddressOption(end: SessaoEndereco): AddressOption {
  const tipoLabel = getEnderecoTipoLabel(end);
  const isMatriz = isEnderecoMatriz(end);

  const nome =
    typeof end?.nome === "string" && end.nome.trim() ? end.nome.trim() : "";

  const linhaPrincipal = [end?.logradouro, end?.numero]
    .filter((x) => typeof x === "string" && String(x).trim().length > 0)
    .map((x) => String(x).trim())
    .join(", ");

  const linhaSecundaria = [end?.bairro, end?.cidade, end?.uf]
    .filter((x) => typeof x === "string" && String(x).trim().length > 0)
    .map((x) => String(x).trim())
    .join(" - ");

  const fallbackLabel =
    typeof end?.label === "string" && end.label.trim()
      ? end.label.trim()
      : [nome, linhaPrincipal, linhaSecundaria].filter(Boolean).join(" • ") ||
        `Endereço ${String(end?.id ?? "")}`;

  const labelComTipo = `${tipoLabel} | ${fallbackLabel}`;

  const lines = [nome || fallbackLabel, linhaPrincipal, linhaSecundaria].filter(
    (x) => typeof x === "string" && x.trim().length > 0
  );

  return {
    value: String(end?.id ?? ""),
    label: labelComTipo,
    tipoLabel,
    isMatriz,
    lines: lines.length > 0 ? lines : [fallbackLabel],
  };
}

const buildSubPath = (
  dept: Department,
  item: MainItem,
  sub: SubItem,
  ancestry: SubItem[] = []
): string => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;
  const chain = [...ancestry, sub]
    .map((entry) => getSubSlug(dept, entry))
    .filter(Boolean);

  return `${baseMainPath}/${chain.join("/")}`;
};

const getRenderableSubitems = (subs: SubItem[] = []): SubItem[] => {
  const walk = (nodes: SubItem[]): SubItem[] => {
    const output: SubItem[] = [];

    for (const node of nodes || []) {
      const children = Array.isArray(node.children) ? walk(node.children) : [];

      if (shouldHideFunctionItem(node)) {
        output.push(...children);
        continue;
      }

      output.push({
        ...node,
        children,
      });
    }

    return output;
  };

  return walk(subs);
};

const getFirstVisibleNestedPath = (
  dept: Department,
  item: MainItem,
  subitems: SubItem[],
  ancestry: SubItem[] = []
): string | null => {
  const renderable = getRenderableSubitems(subitems);

  for (const sub of renderable) {
    const currentPath = buildSubPath(dept, item, sub, ancestry);
    const children = getRenderableSubitems(sub.children || []);

    if (children.length > 0) {
      const deeper = getFirstVisibleNestedPath(dept, item, children, [
        ...ancestry,
        sub,
      ]);

      return deeper || currentPath;
    }

    return currentPath;
  }

  return null;
};

const getMainPath = (dept: Department, item: MainItem) => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const mainSlug = getMainSlug(item);
  const baseMainPath = `${baseDeptPath}/${mainSlug}`;
  const visibleSubs = getRenderableSubitems(item.subitems || []);

  if (
    MAIN_THAT_GO_TO_FIRST_SUB.has(norm(item.label)) &&
    visibleSubs.length > 0
  ) {
    return getFirstVisibleNestedPath(dept, item, visibleSubs) || baseMainPath;
  }

  return baseMainPath;
};

const findDepartmentFromPath = (
  pathname: string,
  departments: Department[]
): Department | null => {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return null;

  const deptFromPath = parts[0];

  return (
    departments.find(
      (d) =>
        norm(getDeptRouteKey(d)) === norm(deptFromPath) ||
        norm(getDeptPermKey(d)) === norm(deptFromPath)
    ) || null
  );
};

const getSubitemPriority = (sub: SubItem) => {
  const label = norm(sub.label);
  const codigo = norm(sub.codigo || "");

  if (codigo === "estoque" || label === "estoque") return 1;
  if (codigo.includes("produtos") || label.includes("produtos")) return 2;
  if (codigo.includes("entradas") || label.includes("entradas")) return 3;
  if (codigo.includes("saidas") || label.includes("saídas")) return 4;
  if (codigo.includes("standby") || label.includes("standby")) return 5;

  if (
    codigo.includes("realizar-movimentacao") ||
    label.includes("realizar movimentacao") ||
    label.includes("realizar movimentação")
  ) {
    return 6;
  }

  if (label.includes("registrar") || label.includes("adicionar")) return 7;

  return 99;
};

const shouldShowSubitem = (sub: SubItem) => {
  if (shouldHideFunctionItem(sub)) return false;

  const label = norm(sub.label);
  const codigo = norm(sub.codigo || "");

  return (
    codigo === "estoque" ||
    label === "estoque" ||
    codigo.includes("realizar-movimentacao") ||
    label.includes("realizar movimentacao") ||
    label.includes("realizar movimentação") ||
    label.includes("registrar") ||
    label.includes("adicionar") ||
    codigo.includes("produtos") ||
    label.includes("produtos") ||
    codigo.includes("entradas") ||
    label.includes("entradas") ||
    codigo.includes("saidas") ||
    label.includes("saidas") ||
    label.includes("saídas") ||
    codigo.includes("standby") ||
    label.includes("standby")
  );
};

const limitFriendlySubitems = (subs: SubItem[]) => {
  const renderable = getRenderableSubitems(subs);

  const filtered = renderable.filter(shouldShowSubitem).sort((a, b) => {
    const pa = getSubitemPriority(a);
    const pb = getSubitemPriority(b);

    if (pa !== pb) return pa - pb;

    return String(a.label || "").localeCompare(String(b.label || ""), "pt-BR");
  });

  return filtered.slice(0, 5);
};

const mergeDepartments = (departments: Department[]): Department[] => {
  const depMap = new Map<string, Department>();

  for (const dept of departments) {
    const depKey = norm(getDeptRouteKey(dept) || getDeptPermKey(dept));
    if (!depKey) continue;

    if (!depMap.has(depKey)) {
      depMap.set(depKey, {
        ...dept,
        items: [],
      });
    }

    const bucket = depMap.get(depKey)!;
    const itemMap = new Map<string, MainItem>();

    for (const existing of bucket.items) {
      itemMap.set(getItemKey(existing), existing);
    }

    for (const item of dept.items || []) {
      const itemKey = getItemKey(item);

      if (!itemKey) continue;

      if (!itemMap.has(itemKey)) {
        itemMap.set(itemKey, item);
      } else {
        itemMap.set(itemKey, mergeMainItem(itemMap.get(itemKey)!, item));
      }
    }

    bucket.items = Array.from(itemMap.values());
  }

  return Array.from(depMap.values()).sort((a, b) => {
    const ao = Number(a.ordem ?? 9999);
    const bo = Number(b.ordem ?? 9999);

    if (ao !== bo) return ao - bo;

    return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  });
};

const departmentKey = (dept: Department) =>
  norm(getDeptRouteKey(dept) || getDeptPermKey(dept) || dept.name || "");

const permissionHasExplicitRestriction = (
  permissions?: ModulePermissions | null
) => {
  if (!permissions || typeof permissions !== "object") return false;

  return (
    permissions.canView !== null ||
    permissions.canCreate !== null ||
    permissions.canUpdate !== null ||
    permissions.canDelete !== null ||
    permissions.canReport !== null
  );
};

const departmentHasExplicitPermission = (dept: Department) => {
  return (dept.items || []).some((item) =>
    permissionHasExplicitRestriction(item.permissions)
  );
};

const cloneDepartmentWithItems = (
  dept: Department,
  items: MainItem[]
): Department => ({
  ...dept,
  items: Array.isArray(items) ? items : [],
});

const mergeDepartmentItems = (
  primaryItems: MainItem[],
  fallbackItems: MainItem[]
): MainItem[] => {
  const itemMap = new Map<string, MainItem>();

  for (const item of primaryItems || []) {
    const key = getItemKey(item);
    if (key) itemMap.set(key, item);
  }

  for (const item of fallbackItems || []) {
    const key = getItemKey(item);
    if (!key) continue;

    if (!itemMap.has(key)) {
      itemMap.set(key, item);
    } else {
      itemMap.set(key, mergeMainItem(itemMap.get(key)!, item));
    }
  }

  return Array.from(itemMap.values());
};

const mergeSpecificAddressDepartmentsWithFallbacks = (
  addressDepartments: Department[],
  fallbackDepartments: Department[]
): Department[] => {
  const resultMap = new Map<string, Department>();

  for (const dept of addressDepartments || []) {
    const key = departmentKey(dept);
    if (!key) continue;

    resultMap.set(key, cloneDepartmentWithItems(dept, dept.items || []));
  }

  for (const fallbackDept of fallbackDepartments || []) {
    const key = departmentKey(fallbackDept);
    if (!key) continue;

    const currentDept = resultMap.get(key);

    if (!currentDept) {
      resultMap.set(
        key,
        cloneDepartmentWithItems(fallbackDept, fallbackDept.items || [])
      );
      continue;
    }

    const currentItems = Array.isArray(currentDept.items)
      ? currentDept.items
      : [];

    const fallbackItems = Array.isArray(fallbackDept.items)
      ? fallbackDept.items
      : [];

    const currentDeptHasExplicitPermission =
      departmentHasExplicitPermission(currentDept);

    if (currentDeptHasExplicitPermission) {
      continue;
    }

    if (currentItems.length === 0 && fallbackItems.length > 0) {
      resultMap.set(key, {
        ...currentDept,
        items: fallbackItems,
        themeColor: currentDept.themeColor || fallbackDept.themeColor,
        iconKey: currentDept.iconKey || fallbackDept.iconKey,
      });
      continue;
    }

    if (currentItems.length > 0 && fallbackItems.length > 0) {
      resultMap.set(key, {
        ...currentDept,
        items: mergeDepartmentItems(currentItems, fallbackItems),
        themeColor: currentDept.themeColor || fallbackDept.themeColor,
        iconKey: currentDept.iconKey || fallbackDept.iconKey,
      });
    }
  }

  return Array.from(resultMap.values()).sort((a, b) => {
    const ao = Number(a.ordem ?? 9999);
    const bo = Number(b.ordem ?? 9999);

    if (ao !== bo) return ao - bo;

    return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  });
};

const formatModuleOrigins = (origins?: AddressOrigin[]): string => {
  const valid = Array.isArray(origins) ? origins.filter(Boolean) : [];

  if (valid.length === 0) return "";

  const visible = valid.slice(0, 2).map((origin) => origin.display);
  const remaining = valid.length - visible.length;

  if (remaining > 0) {
    return `${visible.join(" / ")} +${remaining}`;
  }

  return visible.join(" / ");
};

const pageDepartmentVariants = {
  initial: {
    opacity: 0,
    y: 10,
    filter: "blur(3px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: "blur(3px)",
  },
};

const characterVariants = {
  initial: {
    opacity: 0,
    x: -10,
    scale: 0.985,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    x: -8,
    scale: 0.985,
  },
};

const contentVariants = {
  initial: {
    opacity: 0,
    x: 10,
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: 8,
  },
};

const moduleListVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.035,
      delayChildren: 0.05,
    },
  },
};

const moduleCardVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
};

const ChooseAction: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessao } = useAuth();

  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedAddressValue, setSelectedAddressValue] = useState<string>("");
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);

  const departamentoSessao = String(sessao?.departamento || "");
  const isDev = norm(departamentoSessao) === "desenvolvedor";

  const characterType = useMemo(
    () => getChooseActionCharacterType(location.pathname),
    [location.pathname]
  );

  const sessaoEnderecos = useMemo<SessaoEndereco[]>(() => {
    const value = sessao?.enderecos_disponiveis;
    return Array.isArray(value) ? (value as SessaoEndereco[]) : [];
  }, [sessao?.enderecos_disponiveis]);

  const addressOptions = useMemo<AddressOption[]>(() => {
    const mapped = sessaoEnderecos.map(mapAddressOption);

    return [
      {
        value: ALL_ADDRESSES_VALUE,
        label: "Todos os endereços",
        lines: ["Todos os endereços"],
        tipoLabel: "Todos",
        isAll: true,
      },
      ...mapped,
    ];
  }, [sessaoEnderecos]);

  const defaultAddressValue = useMemo(() => {
    if (
      sessao?.endereco_selecionado_id !== undefined &&
      sessao?.endereco_selecionado_id !== null
    ) {
      return String(sessao.endereco_selecionado_id);
    }

    const matrizAddress = addressOptions.find(
      (opt) => !opt.isAll && opt.isMatriz
    );

    if (matrizAddress) return matrizAddress.value;

    const firstRealAddress = addressOptions.find((opt) => !opt.isAll);
    return firstRealAddress?.value || ALL_ADDRESSES_VALUE;
  }, [sessao?.endereco_selecionado_id, addressOptions]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_ADDRESS);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && parsed.trim()) {
        setSelectedAddressValue(parsed);
      }
    } catch {
      // storage inválido: ignora
    }
  }, []);

  useEffect(() => {
    if (!selectedAddressValue) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const selectedExists = addressOptions.some(
      (opt) => opt.value === selectedAddressValue
    );

    if (!selectedExists && addressOptions.length > 0) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [addressOptions, defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    try {
      if (!selectedAddressValue) return;

      window.localStorage.setItem(
        STORAGE_KEY_SELECTED_ADDRESS,
        JSON.stringify(selectedAddressValue)
      );
    } catch {
      // storage indisponível: ignora
    }
  }, [selectedAddressValue]);

  const selectedAddressOption = useMemo(() => {
    return (
      addressOptions.find((opt) => opt.value === selectedAddressValue) || null
    );
  }, [addressOptions, selectedAddressValue]);

  const baseDepartments = useMemo<Department[]>(() => {
    const value = sessao?.departamentos_acessiveis;
    return Array.isArray(value)
      ? value.map((dept: any) => mapDepartmentFromApi(dept, null))
      : [];
  }, [sessao?.departamentos_acessiveis]);

  const enderecoById = useMemo(() => {
    const map = new Map<string, SessaoEndereco>();

    for (const endereco of sessaoEnderecos || []) {
      const key = String(endereco?.id ?? "");
      if (!key) continue;
      map.set(key, endereco);
    }

    return map;
  }, [sessaoEnderecos]);

  const departmentsByAddress = useMemo(() => {
    const map = new Map<string, Department[]>();

    const rows = Array.isArray(sessao?.enderecos_com_modulos)
      ? (sessao?.enderecos_com_modulos as SessaoEnderecoComModulos[])
      : [];

    for (const row of rows) {
      const key = String(row?.id ?? "");

      const enderecoCompleto = enderecoById.get(key);

      const origin = makeAddressOrigin({
        ...row,
        ...(enderecoCompleto || {}),
      });

      const deps = Array.isArray(row?.departamentos_acessiveis)
        ? row.departamentos_acessiveis.map((dept: any) =>
            mapDepartmentFromApi(dept, origin)
          )
        : [];

      map.set(key, deps);
    }

    return map;
  }, [sessao?.enderecos_com_modulos, enderecoById]);

  const departmentsFromAllAddresses = useMemo<Department[]>(() => {
    const allAddressDepartments = Array.from(
      departmentsByAddress.values()
    ).flat();

    if (allAddressDepartments.length > 0) {
      return mergeDepartments(allAddressDepartments);
    }

    return [];
  }, [departmentsByAddress]);

  const departments = useMemo<Department[]>(() => {
    const fallbackDepartments = mergeDepartments([
      ...departmentsFromAllAddresses,
      ...baseDepartments,
    ]);

    if (selectedAddressValue && selectedAddressValue !== ALL_ADDRESSES_VALUE) {
      const addressDepartments =
        departmentsByAddress.get(String(selectedAddressValue)) || [];

      return mergeSpecificAddressDepartmentsWithFallbacks(
        addressDepartments,
        fallbackDepartments
      );
    }

    if (departmentsFromAllAddresses.length > 0) {
      return mergeSpecificAddressDepartmentsWithFallbacks(
        departmentsFromAllAddresses,
        baseDepartments
      );
    }

    return baseDepartments;
  }, [
    selectedAddressValue,
    departmentsByAddress,
    departmentsFromAllAddresses,
    baseDepartments,
  ]);

  const currentDepartment = useMemo<Department | null>(() => {
    const byPath = findDepartmentFromPath(location.pathname, departments);
    if (byPath) return byPath;

    if (departamentoSessao) {
      const bySession = departments.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(departamentoSessao) ||
          norm(getDeptPermKey(d)) === norm(departamentoSessao) ||
          norm(d.name) === norm(departamentoSessao)
      );

      if (bySession) return bySession;
    }

    return departments[0] || null;
  }, [location.pathname, departments, departamentoSessao]);

  const accentColor = currentDepartment?.themeColor || "#0f333b";

  const transitionDepartmentKey = currentDepartment
    ? `${getDeptRouteKey(currentDepartment)}::${selectedAddressValue}`
    : "sem-departamento";

  const items = useMemo(() => {
    if (!currentDepartment) return [];

    return Array.isArray(currentDepartment.items)
      ? currentDepartment.items.filter((item) => {
          if (item.permissions?.canView === false) return false;
          if (!isDev && item.devOnly === true) return false;
          return true;
        })
      : [];
  }, [currentDepartment, isDev]);

  const shouldShowModuleOrigin =
    selectedAddressValue === ALL_ADDRESSES_VALUE ||
    selectedAddressOption?.isAll === true;

  const goToWithTransition = (path: string) => {
    if (isLeaving) return;

    setIsLeaving(true);

    window.setTimeout(() => {
      navigate(path);
    }, 180);
  };

  if (!sessao) return null;

  if (!currentDepartment) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "calc(100vh - 56px)",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 20px",
          boxSizing: "border-box",
          fontFamily: "'Inter', 'Poppins', sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: 16,
            background: "#ffffff",
            boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              color: "#111827",
              fontWeight: 750,
            }}
          >
            Nenhum módulo disponível
          </h1>

          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              color: "#64748B",
              lineHeight: 1.5,
            }}
          >
            Não há módulos liberados para o seu usuário no endereço selecionado.
          </p>
        </div>
      </div>
    );
  }

  const activeDepartment: Department = currentDepartment;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: "calc(100vh - 56px)",
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 54%, #F3F6F8 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        opacity: isLeaving ? 0 : 1,
        transform: isLeaving ? "translateY(6px)" : "translateY(0)",
        transition:
          "opacity 180ms ease, transform 180ms ease, background 240ms ease",
        fontFamily: "'Inter', 'Poppins', sans-serif",
        overflow: "hidden",
      }}
      onClick={() => setAddressDropdownOpen(false)}
    >
      <div
        style={{
          position: "absolute",
          top: 80,
          right: 28,
          zIndex: 30,
          width: 318,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setAddressDropdownOpen((prev) => !prev)}
          style={{
            width: "100%",
            minHeight: 46,
            border: "1px solid rgba(15,23,42,0.10)",
            borderRadius: 10,
            background: "#FFFFFF",
            boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
            padding: "7px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            textAlign: "left",
            transition:
              "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <MapPin size={15} color={accentColor} />

            <span
              style={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#64748B",
                }}
              >
                Endereço
              </span>

              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: 10.5,
                    fontWeight: 750,
                    color: "#475569",
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                    letterSpacing: "0.025em",
                  }}
                >
                  {selectedAddressOption?.tipoLabel || "Unidade"}
                </span>

                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    width: 1,
                    height: 13,
                    background: "rgba(100,116,139,0.35)",
                  }}
                />

                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#111827",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 210,
                  }}
                >
                  {selectedAddressOption?.lines?.[0] || "Selecionar"}
                </span>
              </span>
            </span>
          </span>

          <ChevronDown
            size={15}
            color="#64748B"
            style={{
              transform: addressDropdownOpen
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 160ms ease",
              flexShrink: 0,
            }}
          />
        </button>

        {addressDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              width: "100%",
              maxHeight: 280,
              overflowY: "auto",
              background: "#FFFFFF",
              border: "1px solid rgba(15,23,42,0.08)",
              borderRadius: 12,
              boxShadow: "0 18px 38px rgba(15,23,42,0.12)",
              padding: 6,
              zIndex: 40,
            }}
          >
            {addressOptions.map((option) => {
              const selected = option.value === selectedAddressValue;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSelectedAddressValue(option.value);
                    setAddressDropdownOpen(false);
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    background: selected
                      ? "rgba(15,23,42,0.045)"
                      : "transparent",
                    borderRadius: 8,
                    padding: "9px 10px",
                    margin: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 750,
                        color: "#475569",
                        lineHeight: 1.2,
                        textTransform: "uppercase",
                        letterSpacing: "0.025em",
                      }}
                    >
                      {option.tipoLabel || "Unidade"}
                    </span>

                    <span
                      aria-hidden="true"
                      style={{
                        flexShrink: 0,
                        width: 1,
                        height: 12,
                        background: "rgba(100,116,139,0.32)",
                      }}
                    />

                    <span
                      style={{
                        color: "#111827",
                        fontSize: 12.2,
                        fontWeight: selected ? 750 : 700,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.lines[0]}
                    </span>
                  </span>

                  {option.lines.slice(1).map((line, index) => (
                    <span
                      key={`${option.value}-${index}`}
                      style={{
                        color: "#64748B",
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.25,
                        paddingLeft: 2,
                      }}
                    >
                      {line}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={transitionDepartmentKey}
          variants={pageDepartmentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{
            duration: 0.26,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            width: "100%",
            maxWidth: 1120,
            display: "grid",
            gridTemplateColumns: "360px minmax(0, 1fr)",
            gap: 40,
            alignItems: "center",
            willChange: "opacity, transform, filter",
          }}
        >
          <motion.div
            variants={characterVariants}
            transition={{
              duration: 0.28,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.95,
              minHeight: 430,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 330,
                height: 430,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                userSelect: "none",
                filter: isLeaving ? "blur(1px)" : "none",
                transform: `translateY(${
                  CHARACTER_VERTICAL_OFFSET + (isLeaving ? 6 : 0)
                }px) scale(${isLeaving ? 0.985 : 1})`,
                transition: "filter 180ms ease, transform 180ms ease",
                overflow: "visible",
              }}
            >
              {characterType === "governanca" ? (
                <img
                  src={Person01DepartamentoGovernancaPng}
                  alt="Governança, Qualidade e Compliance"
                  draggable={false}
                  style={{
                    width: "100%",
                    maxWidth: 330,
                    maxHeight: 430,
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    userSelect: "none",
                  }}
                />
              ) : characterType === "tecnico" ? (
                <img
                  src={Person01DepartamentoTecnicoPng}
                  alt="Departamento Técnico"
                  draggable={false}
                  style={{
                    width: "100%",
                    maxWidth: 330,
                    maxHeight: 430,
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    userSelect: "none",
                  }}
                />
              ) : (
                <DefaultChooseActionSvg accentColor={accentColor} />
              )}
            </div>
          </motion.div>

          <motion.div
            variants={contentVariants}
            transition={{
              duration: 0.26,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              minWidth: 0,
              opacity: isLeaving ? 0.92 : 1,
              transform: isLeaving ? "translateX(4px)" : "translateX(0)",
              transition: "opacity 180ms ease, transform 180ms ease",
            }}
          >
            <div
              style={{
                marginBottom: 18,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 750,
                    color: accentColor,
                    marginBottom: 6,
                    letterSpacing: "0.02em",
                    transition: "color 220ms ease",
                  }}
                >
                  {activeDepartment.name}
                </div>

                <h1
                  style={{
                    margin: 0,
                    fontSize: 24,
                    lineHeight: 1.15,
                    fontWeight: 750,
                    color: "#111827",
                    fontFamily: "'Poppins', 'Inter', sans-serif",
                  }}
                >
                  Qual módulo você gostaria de acessar?
                </h1>

                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 13,
                    color: "#64748B",
                    lineHeight: 1.45,
                  }}
                >
                  Os módulos abaixo respeitam as permissões do usuário, endereço
                  e departamento.
                </p>
              </div>
            </div>

            {items.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.22,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: 16,
                  background: "#FFFFFF",
                  padding: 22,
                  color: "#64748B",
                  fontSize: 13,
                  lineHeight: 1.5,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                }}
              >
                Nenhum módulo liberado para este departamento no endereço
                selecionado.
              </motion.div>
            ) : (
              <motion.div
                variants={moduleListVariants}
                initial="initial"
                animate="animate"
                style={{
                  display: "grid",
                  gap: 10,
                }}
              >
                {items.map((item, index) => {
                  const friendlySubs = limitFriendlySubitems(
                    item.subitems || []
                  );
                  const mainPath = getMainPath(activeDepartment, item);
                  const itemAccent = item.themeColor || accentColor;
                  const moduleOriginsText = formatModuleOrigins(
                    item.addressOrigins
                  );

                  return (
                    <motion.div
                      key={`${item.id ?? item.codigo ?? item.label}-${index}`}
                      variants={moduleCardVariants}
                      transition={{
                        duration: 0.22,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      style={{
                        border: `1px solid ${itemAccent}20`,
                        borderRadius: 14,
                        background: "#FFFFFF",
                        padding: 14,
                        boxShadow: "0 4px 14px rgba(15,23,42,0.035)",
                        transition:
                          "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 10px 22px rgba(15,23,42,0.06)";
                        e.currentTarget.style.borderColor = `${itemAccent}42`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 14px rgba(15,23,42,0.035)";
                        e.currentTarget.style.borderColor = `${itemAccent}20`;
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => goToWithTransition(mainPath)}
                        style={{
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              marginBottom: 3,
                            }}
                          >
                            <ChevronRight size={14} color={itemAccent} />

                            <span
                              style={{
                                fontSize: 15,
                                fontWeight: 750,
                                color: "#111827",
                                lineHeight: 1.2,
                              }}
                            >
                              {item.label}
                            </span>
                          </div>
                        </div>

                        <ArrowRight size={16} color={itemAccent} />
                      </button>

                      {shouldShowModuleOrigin && moduleOriginsText && (
                        <div
                          style={{
                            marginTop: 8,
                            paddingLeft: 21,
                            color: "#64748B",
                            fontSize: 11.2,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {moduleOriginsText}
                        </div>
                      )}

                      {friendlySubs.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            marginTop: 12,
                            paddingLeft: 21,
                          }}
                        >
                          {friendlySubs.map((sub, subIndex) => (
                            <button
                              key={`${sub.label}-${sub.codigo ?? subIndex}`}
                              type="button"
                              onClick={() =>
                                goToWithTransition(
                                  buildSubPath(activeDepartment, item, sub)
                                )
                              }
                              style={{
                                border: `1px solid ${itemAccent}20`,
                                background: `${itemAccent}0D`,
                                color: itemAccent,
                                borderRadius: 999,
                                padding: "7px 11px",
                                fontSize: 11.5,
                                fontWeight: 700,
                                cursor: "pointer",
                                lineHeight: 1.15,
                                transition:
                                  "transform 140ms ease, background 140ms ease, border-color 140ms ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(-1px)";
                                e.currentTarget.style.background = `${itemAccent}16`;
                                e.currentTarget.style.borderColor = `${itemAccent}42`;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                e.currentTarget.style.background = `${itemAccent}0D`;
                                e.currentTarget.style.borderColor = `${itemAccent}20`;
                              }}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ChooseAction;
